import React from 'react';
import BodyMapSVG from './BodyMapSVG';

/**
 * WorkoutMuscleMap — the muscle-group body silhouette for a workout.
 *
 * This is the "which body parts are we working" visual the LLM drives:
 * Nikki returns primary/secondary muscle groups, and we highlight them on
 * the anatomical body (lime = primary, amber = secondary) via BodyMapSVG's
 * `exercise` highlight mode. Used on the Today card, Completed grid, and
 * Library so every workout reads as a body, not an icon.
 *
 * DEFAULT_MUSCLES mirrors the backend `_default_muscles()` so legacy plans
 * and timer sessions (which may not have stored muscle groups) still render.
 */
export const DEFAULT_MUSCLES = {
  strength_upper:    { primary: ['chest', 'shoulders', 'upper_back', 'biceps', 'triceps'], secondary: ['core'] },
  strength_lower:    { primary: ['quads', 'hamstrings', 'glutes', 'calves'], secondary: ['lower_back', 'core'] },
  strength_full:     { primary: ['chest', 'quads', 'upper_back', 'glutes'], secondary: ['core', 'shoulders', 'hamstrings'] },
  hybrid:            { primary: ['quads', 'glutes', 'chest', 'core'], secondary: ['shoulders', 'hamstrings'] },
  mobility:          { primary: ['shoulders', 'upper_back', 'glutes', 'hamstrings'], secondary: ['lower_back', 'core'] },
  yoga:              { primary: ['core', 'hamstrings', 'glutes', 'shoulders'], secondary: ['lower_back', 'upper_back'] },
  injury_management: { primary: ['lower_back', 'core', 'glutes'], secondary: ['hamstrings'] },
  walk:              { primary: ['quads', 'hamstrings', 'glutes', 'calves'], secondary: [] },
  breathwork:        { primary: ['core'], secondary: [] },
};

// Resolve muscle groups from a suggestion/plan/session object, falling back
// to modality defaults when none are stored.
export const musclesFor = (src = {}, modality) => {
  const primary = (src.primary_muscles && src.primary_muscles.length) ? src.primary_muscles : null;
  if (primary) return { primary, secondary: src.secondary_muscles || [] };
  const d = DEFAULT_MUSCLES[modality] || DEFAULT_MUSCLES.strength_full;
  return { primary: d.primary, secondary: d.secondary };
};

const WorkoutMuscleMap = ({ primary = [], secondary = [], size = 'sm', showBack = false, className = '' }) => (
  <BodyMapSVG
    highlightMode="exercise"
    primaryMuscles={primary}
    secondaryMuscles={secondary}
    size={size}
    showBack={showBack}
    showLabels={false}
    interactive={false}
    className={className}
  />
);

export default WorkoutMuscleMap;
